import { action, computed, makeObservable, observable, runInAction } from 'mobx';
import { createContext, useContext } from 'react';
import { bpHistory } from 'global/bpHistory';
import ElectionsModule from 'store/modules/elections/electionsModule';
import EmployeeProfileTab from './employeeProfileTab';

export interface FullEmployeeFormDomainParams {
  groupId: string;
  periodId: string;
  subscriberId?: string;
  activeTabId?: string;
}

export enum TabId {
  EMPLOYEE_PROFILE = 'employee-profile',
  DEPENDENTS = 'dependents',
  ELECTIONS = 'elections',
}

function isTabId(value: string): value is TabId {
  return Object.values(TabId).includes(value as TabId);
}

function syncSubscriberIdToUrl(subscriberId: string): void {
  const params = new URLSearchParams(bpHistory.location.search);
  params.set('subscriberId', subscriberId);
  bpHistory.replace({
    pathname: bpHistory.location.pathname,
    search: params.toString(),
  });
}

// TO_DO_QUOTE_CHECKLIST: Move this page to the enrollment-checklist flow.
export default class FullFormPageStore {
  private _domainData: FullEmployeeFormDomainParams;
  private _currentTabId: TabId = TabId.EMPLOYEE_PROFILE;
  private _isInitialDataLoading = true;
  private _isLoading = false;
  private _employeeProfileTab: EmployeeProfileTab;

  private async _resetPage(): Promise<void> {
    this._currentTabId = TabId.EMPLOYEE_PROFILE;
    this._electionsModule.resetSubscribers();
    this._domainData.subscriberId = undefined;
    await this.employeeProfileTab.fetchForm();
  }

  private _electionsModule: ElectionsModule;

  public get domainData(): FullEmployeeFormDomainParams {
    return this._domainData;
  }

  public get electionsModule(): ElectionsModule {
    return this._electionsModule;
  }

  public get currentTabId(): TabId {
    return this._currentTabId;
  }

  public get employeeProfileTab(): EmployeeProfileTab {
    return this._employeeProfileTab;
  }

  public get subscriberExists(): boolean {
    return this._electionsModule.subscribersExist;
  }

  public get aggregatedErrors(): string[] {
    return this._employeeProfileTab.errors;
  }

  public get isInitialDataLoading(): boolean {
    return this._isInitialDataLoading;
  }

  public get isLoading(): boolean {
    return this._isLoading;
  }

  private _setIsInitialDataLoading(isLoading: boolean): void {
    this._isInitialDataLoading = isLoading;
  }

  private _setIsLoading(isLoading: boolean): void {
    this._isLoading = isLoading;
  }

  public setCurrentTabId(tabId: string): void {
    if (!isTabId(tabId)) {
      throw new Error(`Invalid tab ID: ${tabId}`);
    }
    this._currentTabId = tabId;
  }

  public async getInitialData(): Promise<void> {
    this._setIsInitialDataLoading(true);
    await this._electionsModule.fetchElectionsCoreData();
    await runInAction(async () => {
      if (this._domainData.subscriberId) {
        await this._electionsModule.fetchSubscriberById(this._domainData.subscriberId);
      }
    });
    await runInAction(async () => {
      await this.employeeProfileTab.fetchForm();
    });
    runInAction(() => {
      this._setIsInitialDataLoading(false);
    });
  }

  public async updateSubscriberData(): Promise<void> {
    if (!this._domainData.subscriberId) return;
    this._setIsLoading(true);
    await this._electionsModule.fetchSubscriberById(this._domainData.subscriberId);
    runInAction(() => {
      this._setIsLoading(false);
    });
  }

  public async setupPageForNewEmployee(subscriberId: string): Promise<void> {
    this._electionsModule = new ElectionsModule({
      groupId: this._domainData.groupId,
      enrollmentPeriodId: this._domainData.periodId,
    });
    this._setIsInitialDataLoading(true);
    await this._electionsModule.fetchElectionsCoreData();
    await runInAction(async () => {
      await this._electionsModule.fetchSubscriberById(subscriberId);
    });
    await runInAction(async () => {
      await this.employeeProfileTab.fetchForm();
    });

    runInAction(() => {
      this._domainData.subscriberId = subscriberId;
      syncSubscriberIdToUrl(subscriberId);
      this._setIsInitialDataLoading(false);
    });
  }

  public onBackRedirect(): void {
    bpHistory.push(`/add-employees/${this._domainData.groupId}/${this._domainData.periodId}`);
  }

  public async onAddAnotherEmployee(): Promise<void> {
    await this._resetPage();
  }

  constructor(domainData: FullEmployeeFormDomainParams) {
    this._domainData = domainData;

    this._electionsModule = new ElectionsModule({
      groupId: this._domainData.groupId,
      enrollmentPeriodId: this._domainData.periodId,
    });
    if (this._domainData.activeTabId) {
      if (!isTabId(this._domainData.activeTabId)) {
        throw new Error(
          `Invalid tab ID: ${this._domainData.activeTabId} . Allowed tabs are: ${Object.values(TabId).join(', ')}`,
        );
      }
      this._currentTabId = this._domainData.activeTabId;
    }
    this._employeeProfileTab = new EmployeeProfileTab(this._domainData, this);

    makeObservable<
      FullFormPageStore,
      | '_domainData'
      | '_currentTabId'
      | '_electionsModule'
      | '_isLoading'
      | '_resetPage'
      | '_isInitialDataLoading'
      | '_setIsInitialDataLoading'
      | '_setIsLoading'
    >(this, {
      _domainData: observable,
      _currentTabId: observable,
      _electionsModule: observable,
      _isInitialDataLoading: observable,
      _isLoading: observable,
      currentTabId: computed,
      domainData: computed,
      electionsModule: computed,
      subscriberExists: computed,
      aggregatedErrors: computed,
      isInitialDataLoading: computed,
      isLoading: computed,
      _setIsInitialDataLoading: action.bound,
      _setIsLoading: action.bound,
      _resetPage: action.bound,
      setCurrentTabId: action.bound,
      onBackRedirect: action.bound,
      onAddAnotherEmployee: action.bound,
      getInitialData: action.bound,
      updateSubscriberData: action.bound,
      setupPageForNewEmployee: action.bound,
    });
  }
}

const FullFormPageStoreContext = createContext(new FullFormPageStore({} as FullEmployeeFormDomainParams));

export const useFullFormPageStore = (): FullFormPageStore => {
  return useContext(FullFormPageStoreContext);
};

export { FullFormPageStoreContext };
